import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  MapPin, Phone, FolderOpen, CheckCircle, Crosshair, Calendar as CalIcon,
} from "@phosphor-icons/react";
import MobileShell from "@/mobile/MobileShell";
import { computeEventState, STATE_STYLES } from "@/mobile/eventStatus";

const hhmm = (iso) => new Date(iso).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
const fmtDateLong = (iso) =>
  new Date(iso).toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });

export default function MobileEventDetail() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);
  const [exp, setExp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      // Pedimos un rango amplio (90 días) para que el GET /events nos lo encuentre,
      // tanto si es derivado (`exp:...`) como persistido.
      const today = new Date();
      const from = new Date(today);
      from.setDate(today.getDate() - 30);
      const to = new Date(today);
      to.setDate(today.getDate() + 60);
      const ymd = (d) => d.toISOString().slice(0, 10);
      const r = await api.get("/calendar/events", { params: { date_from: ymd(from), date_to: ymd(to) } });
      const found = (r.data || []).find((e) => e.id === eventId);
      if (!found) {
        toast.error("No se ha encontrado la visita");
        navigate("/m/agenda");
        return;
      }
      setEv(found);
      if (found.expediente_id) {
        try {
          const ex = await api.get(`/expedientes/${found.expediente_id}`);
          setExp(ex.data);
        } catch { /* expediente borrado o sin permisos */ }
      }
    } finally {
      setLoading(false);
    }
  }, [eventId, navigate]);

  useEffect(() => { reload(); }, [reload]);

  const startVisit = async (via = "manual") => {
    if (!ev) return;
    setStarting(true);
    try {
      const r = await api.post(`/calendar/events/${encodeURIComponent(ev.id)}/start-visit`, { via });
      setEv((prev) => ({ ...prev, ...r.data }));
      toast.success(via === "gps" ? "Visita iniciada (GPS)" : "Visita iniciada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar la visita");
    } finally {
      setStarting(false);
    }
  };

  const tryGpsStart = () => {
    if (!("geolocation" in navigator)) {
      toast.error("Tu navegador no expone GPS");
      return;
    }
    toast.message("Buscando tu posición…");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        // Si tenemos lat/lon del expediente (Catastro), validamos distancia.
        const lat = exp?.catastro_lat;
        const lon = exp?.catastro_lon;
        if (lat && lon) {
          const d = haversineMeters(pos.coords.latitude, pos.coords.longitude, lat, lon);
          if (d > 300) {
            toast.error(`Estás a ${Math.round(d)} m del lugar. Usa "Iniciar manual".`);
            return;
          }
        }
        startVisit("gps");
      },
      (err) => toast.error("GPS denegado: " + (err.message || "")),
      { enableHighAccuracy: true, timeout: 12_000 }
    );
  };

  if (loading || !ev) {
    return (
      <MobileShell title="Visita" backTo="/m/agenda">
        <div className="text-center text-muted text-xs py-10">Cargando…</div>
      </MobileShell>
    );
  }

  const state = computeEventState(ev);
  const sty = STATE_STYLES[state];
  const ed = exp?.extracted_data || {};
  const numSiniestro = ed.numero_siniestro || ev.expediente_numero || "—";
  const direccion = ev.location || [ed.direccion_riesgo, ed.codigo_postal, ed.municipio].filter(Boolean).join(", ");
  const nombre = ev.asegurado_nombre || ed.asegurado_nombre || "—";
  const tel = ev.asegurado_movil || ev.asegurado_telefono || ed.asegurado_movil || ed.asegurado_telefono;
  const observ = exp?.observaciones_encargo || exp?.anotacion_encargo;
  const circ = ed.observaciones || exp?.observaciones_siniestro;

  return (
    <MobileShell title="Visita" backTo="/m/agenda">
      {/* Cabecera */}
      <div className={`px-3 py-3 border-b border-[#E8EAED] ${sty.card}`}>
        <div className="flex items-center gap-2">
          <CalIcon className="w-4 h-4 text-muted" weight="bold" />
          <div className="text-[12px] text-muted capitalize">{fmtDateLong(ev.starts_at)} · {hhmm(ev.starts_at)}</div>
          <span className={`ml-auto px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ${sty.badge}`}>
            {sty.label}
          </span>
        </div>
        <div className="mt-1 text-[18px] font-bold leading-tight">{nombre}</div>
        <div className="text-[12px] text-muted">Nº {numSiniestro}</div>
      </div>

      {/* Acciones principales */}
      <div className="px-3 pt-3 grid grid-cols-3 gap-2">
        {direccion && (
          <ActionPill
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(direccion)}`}
            external
            icon={<MapPin className="w-4 h-4" weight="bold" />}
            label="Cómo llegar"
            testid="event-action-map"
          />
        )}
        {tel && (
          <ActionPill
            href={`tel:${tel}`}
            icon={<Phone className="w-4 h-4" weight="bold" />}
            label="Llamar"
            testid="event-action-call"
          />
        )}
        {ev.expediente_id && (
          <ActionPill
            onClick={() => navigate(`/m/expediente/${ev.expediente_id}`)}
            icon={<FolderOpen className="w-4 h-4" weight="bold" />}
            label="Expediente"
            testid="event-action-open-exp"
          />
        )}
      </div>

      {/* Datos del siniestro */}
      <Section title="Detalle">
        <Field label="Dirección" value={direccion || "—"} testid="event-direccion" />
        <Field label="Asegurado" value={nombre} testid="event-asegurado" />
        {tel && <Field label="Teléfono" value={tel} testid="event-telefono" />}
        {ed.causa && <Field label="Causa" value={ed.causa} testid="event-causa" />}
        {ed.ramo && <Field label="Ramo" value={ed.ramo} testid="event-ramo" />}
      </Section>

      {observ && (
        <Section title="Observaciones del encargo" subtitle="Notas que dejó el gestor para esta visita.">
          <p className="text-[13px] whitespace-pre-wrap leading-relaxed" data-testid="event-observaciones-encargo">{observ}</p>
        </Section>
      )}

      {circ && (
        <Section title="Circunstancias del siniestro">
          <p className="text-[13px] whitespace-pre-wrap leading-relaxed" data-testid="event-circunstancias">{circ}</p>
        </Section>
      )}

      {/* Iniciar visita (sticky bottom si pending/delayed) */}
      {state !== "visited" && state !== "cancelled" && (
        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-[#E8EAED] px-3 py-3 flex gap-2 z-10">
          <button
            onClick={tryGpsStart}
            disabled={starting}
            className="flex-1 inline-flex items-center justify-center gap-1.5 bg-white border border-[#E8EAED] text-foreground text-[13px] font-semibold py-3 rounded-md disabled:opacity-50"
            data-testid="event-start-gps"
          >
            <Crosshair className="w-4 h-4" weight="bold" />
            GPS
          </button>
          <button
            onClick={() => startVisit("manual")}
            disabled={starting}
            className="flex-[2] inline-flex items-center justify-center gap-1.5 bg-emerald-600 text-white text-[14px] font-semibold py-3 rounded-md disabled:opacity-50 hover:bg-emerald-700"
            data-testid="event-start-manual"
          >
            <CheckCircle className="w-4 h-4" weight="fill" />
            Marcar visita iniciada
          </button>
        </div>
      )}
    </MobileShell>
  );
}

function ActionPill({ href, external, onClick, icon, label, testid }) {
  const cls =
    "inline-flex flex-col items-center justify-center gap-1 py-3 rounded-md bg-white border border-[#E8EAED] hover:bg-zinc-50 active:translate-y-px";
  if (href) {
    return (
      <a
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
        className={cls}
        data-testid={testid}
      >
        {icon}
        <span className="text-[11px] font-semibold">{label}</span>
      </a>
    );
  }
  return (
    <button onClick={onClick} className={cls} data-testid={testid}>
      {icon}
      <span className="text-[11px] font-semibold">{label}</span>
    </button>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="mt-3 mx-3 bg-white rounded-lg border border-[#E8EAED] p-3">
      <div className="text-[11px] uppercase tracking-wide font-semibold text-muted">{title}</div>
      {subtitle && <div className="text-[11px] text-muted mt-0.5">{subtitle}</div>}
      <div className="mt-2 space-y-1.5">{children}</div>
    </section>
  );
}

function Field({ label, value, testid }) {
  return (
    <div className="flex items-baseline gap-2">
      <div className="w-24 text-[11px] text-muted">{label}</div>
      <div className="flex-1 text-[13px]" data-testid={testid}>{value}</div>
    </div>
  );
}

function haversineMeters(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}
